/**
 * Standard tournament terms (#2691), each worded for organizers and players.
 *
 * Staff ⓘ buttons in setup and the "Tournament terms" glossary use `staff`;
 * the player event list uses `player`. Wording follows the approved
 * "Tournament setup, simplified" frames (F1, F2, F3, F25).
 */

export type TournamentTermKey =
  | "draw"
  | "draw_size"
  | "bye"
  | "seed"
  | "level"
  | "age_group"
  | "consolation"
  | "knockout"
  | "round_robin"
  | "groups_knockout"
  | "double_elimination"
  | "places_playout"
  | "guaranteed_matches"
  | "walkover"
  | "waitlist"
  | "entry_limit"
  | "court_hours"
  | "warm_up"
  | "minimum_rest"
  | "official_event";

export interface TournamentTerm {
  key: TournamentTermKey;
  label: string;
  staff: string;
  player: string;
}

export const TOURNAMENT_TERMS: readonly TournamentTerm[] = [
  {
    key: "bye",
    label: "Bye",
    staff:
      "A free pass to the next round when the draw has an empty spot. A bye isn’t a match played.",
    player:
      "A free pass to the next round when nobody fills your spot. It doesn’t count as a match played.",
  },
  {
    key: "consolation",
    label: "Consolation",
    staff:
      "A second bracket for players who lose their first match, so everyone plays at least twice. Some clubs call it a plate.",
    player:
      "If you lose your first match, you keep playing in a second bracket, so you play at least twice.",
  },
  {
    key: "court_hours",
    label: "Court-hours",
    staff:
      "One court for one hour. Each match uses warm-up + match length + changeover of court time.",
    player: "One court for one hour; the organizer uses it to plan the day.",
  },
  {
    key: "double_elimination",
    label: "Double elimination",
    staff:
      "Out after two losses: a winners bracket and a losers bracket, with a final between them.",
    player: "You’re out after your second loss.",
  },
  {
    key: "draw",
    label: "Draw",
    staff:
      "The bracket or groups for one event: who plays whom, and in what order. Brackets are made after registration closes.",
    player:
      "The bracket or groups for your event: who you play, and when. It’s made after entries close.",
  },
  {
    key: "draw_size",
    label: "Draw size",
    staff:
      "How many spots a bracket has (8, 16, 32). Spots without an entry become byes.",
    player: "How many spots the bracket has. Empty spots become byes.",
  },
  {
    key: "entry_limit",
    label: "Entry limit",
    staff:
      "The most players or pairs an event takes. It can go up any time; it can’t go below the entries already in.",
    player: "How many players or pairs the event takes.",
  },
  {
    key: "groups_knockout",
    label: "Round-robin groups",
    staff:
      "Groups of 4 or more play everyone in their group; the top 2 of each group go to a knockout.",
    player:
      "You play everyone in a group of 4 or more; the top 2 of each group go on to a knockout.",
  },
  {
    key: "guaranteed_matches",
    label: "Guaranteed matches",
    staff:
      "The fewest matches an entry plays, if you play your first match (not after a walkover or withdrawal).",
    player:
      "The fewest matches you’ll play, as long as you play your first match.",
  },
  {
    key: "knockout",
    label: "Knockout",
    staff: "Lose once and you’re out. One match is guaranteed.",
    player: "Lose once and you’re out.",
  },
  {
    key: "level",
    label: "Level",
    staff:
      "A skill group so players meet similar opponents. In the US, Open is the top level and anyone may enter; then A, B and C. Players pick their level when they enter, and you can move them before the bracket is made.",
    player:
      "A skill group so you meet similar opponents. Open is the top level and anyone may enter; then A, B and C. Pick the level that fits you.",
  },
  {
    key: "age_group",
    label: "Age group",
    staff:
      "Events for an age band, like U15 (under 15) or 35+. Age is measured on the event’s “age as of” date.",
    player:
      "Events for an age band, like U15 (under 15) or 35+, measured on the event’s “age as of” date.",
  },
  {
    key: "minimum_rest",
    label: "Minimum rest",
    staff:
      "The shortest break a player gets between their matches, including across events.",
    player: "The shortest break you get between your matches.",
  },
  {
    key: "official_event",
    label: "Official BW tournament",
    staff:
      "A tournament whose results count toward BW ratings. Apply before you open registration; it needs a referee.",
    player: "Results from an official BW tournament count toward your BW rating.",
  },
  {
    key: "places_playout",
    label: "Places playout",
    staff: "Every place is played for, so every entry plays every round.",
    player: "Every place is played for, so you play every round.",
  },
  {
    key: "round_robin",
    label: "Round robin",
    staff: "Everyone plays everyone. Up to 8 entries.",
    player: "You play everyone in the event.",
  },
  {
    key: "seed",
    label: "Seed",
    staff:
      "An entry placed apart in the draw so the strongest can’t meet early. Seeds come from BW ratings or you set them.",
    player:
      "A top entry placed apart in the bracket so the strongest players don’t meet early.",
  },
  {
    key: "waitlist",
    label: "Waitlist",
    staff:
      "When an event is full, players can join a waitlist and move in, in order, when a spot frees up.",
    player:
      "When an event is full you can join the waitlist; you move in, in order, when a spot opens.",
  },
  {
    key: "walkover",
    label: "Walkover",
    staff:
      "A win given when the opponent doesn’t play. It counts as a win, not as a match played.",
    player:
      "A win when your opponent doesn’t play. It’s a win, but not a match played.",
  },
  {
    key: "warm_up",
    label: "Warm-up",
    staff:
      "Time on court before each match. It’s part of every match’s court time; warming up off court frees it.",
    player: "A few minutes on court before your match starts.",
  },
] as const;

const BY_KEY = new Map(TOURNAMENT_TERMS.map((term) => [term.key, term]));

export function tournamentTerm(key: TournamentTermKey): TournamentTerm {
  const term = BY_KEY.get(key);
  if (!term) throw new Error(`Unknown tournament term: ${key}`);
  return term;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** A regular expression matching words that start with the query. */
export function termSearchPattern(query: string): RegExp | null {
  const needle = query.trim();
  return needle ? new RegExp(`\\b${escapeRegExp(needle)}`, "gi") : null;
}

/** Terms with a word in the label or wording that starts with the query,
 * case-insensitive ("draw" finds "draw size", not "withdrawal"). */
export function searchTerms(
  query: string,
  audience: "staff" | "player" = "staff",
): TournamentTerm[] {
  const pattern = termSearchPattern(query);
  if (!pattern) return [...TOURNAMENT_TERMS];
  const test = (text: string) => {
    pattern.lastIndex = 0;
    return pattern.test(text);
  };
  return TOURNAMENT_TERMS.filter(
    (term) => test(term.label) || test(term[audience]),
  );
}
